/**
 * /login
 * Admin enters their email. If allow-listed, we send them a magic link.
 * The page intentionally does NOT confirm whether their email is on the
 * allow-list, so the API can't be probed for valid admin addresses.
 */

import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE } from '../lib/auth';

export default function Login() {
  const [params] = useSearchParams();
  const justClaimed = params.get('claimed') === '1';

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('That does not look like an email address.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      if (res.ok) {
        setSent(true);
      } else {
        setError(
          "Something went wrong on our end. Take a breath and try again in a minute."
        );
      }
    } catch {
      setError(
        "We couldn't reach the server. Check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Shell>
      {justClaimed && !sent && (
        <div className="mb-5 px-4 py-3 rounded-xl border border-teal/30 bg-teal/[0.06] text-[13px] text-burgundy-soft">
          Dashboard claimed. Enter your email below to sign in.
        </div>
      )}

      {sent ? (
        <>
          <h1 className="font-bagel text-[36px] text-burgundy mb-2">Check your inbox.</h1>
          <p className="text-burgundy-soft text-[15px] leading-relaxed">
            If <strong className="text-burgundy">{email}</strong> is on the
            admin list, a sign-in link is on its way. It is good for 15
            minutes and only works once.
          </p>
          <p className="text-burgundy-faint text-[13px] mt-6">
            No email after a minute? Check spam, then{' '}
            <button
              type="button"
              onClick={() => {
                setSent(false);
                setError('');
              }}
              className="text-pink-deep underline font-semibold hover:text-burgundy"
            >
              try again
            </button>
            .
          </p>
        </>
      ) : (
        <>
          <h1 className="font-bagel text-[36px] text-burgundy mb-2">Sign in.</h1>
          <p className="text-burgundy-soft text-[15px] leading-relaxed mb-6">
            Enter your admin email. We will send you a one-time sign-in link.
          </p>

          {error && (
            <div
              role="alert"
              className="mb-4 px-4 py-3 rounded-xl border border-danger bg-danger/[0.08] text-[13px] font-semibold text-danger"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-burgundy-faint">
                Email
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
                placeholder="you@example.com"
                className="px-3 py-2.5 bg-cream border border-burgundy/[0.2] rounded-lg font-hanken text-[14px] text-burgundy placeholder:text-burgundy-faint focus:outline-none focus:border-pink focus:shadow-[0_0_0_3px_rgba(238,58,140,0.12)] transition-[border-color,box-shadow]"
              />
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="bg-pink text-cream-bright px-6 py-3 rounded-xl font-bold text-[14px] tracking-[0.3px] hover:bg-pink-deep disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Sending...' : 'Send me the link'}
            </button>
          </form>
        </>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-full bg-cream-soft text-burgundy font-hanken flex flex-col">
      <div className="h-[60px] bg-forest text-cream-bright flex items-center px-6 flex-shrink-0">
        <div className="font-rye text-[18px] tracking-[0.5px] text-cream-bright">
          <DotI>I</DotI> SAID WHAT <DotI>I</DotI> SAID
        </div>
      </div>
      <main className="flex-1 flex justify-center px-4 sm:px-8 py-12">
        <div className="w-full max-w-[440px] bg-cream-bright border border-burgundy/[0.12] rounded-2xl p-6 sm:p-10 shadow-sm">
          {children}
        </div>
      </main>
    </div>
  );
}

function DotI({ children }: { children: React.ReactNode }) {
  return (
    <span className="relative inline-block">
      <span
        className="absolute left-1/2 -translate-x-1/2 w-1 h-1 bg-pink rounded-full"
        style={{ top: '-6px' }}
        aria-hidden="true"
      />
      {children}
    </span>
  );
}
